// src/components/admin/UsersTab.tsx
import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Avatar,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Stack,
  Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import EditIcon from "@mui/icons-material/Edit";
import {
  useAdminUsers,
  useUpdateUserRole,
  useDeactivateUser,
  useAddUserToTeam,
  useRemoveUserFromTeam,
  useAdminTeams,
  useGetUserModules,
  useSetUserModules,
  UserModuleAccess,
} from "../../hooks/useAdminData";
import { useUserRole } from "../../hooks/useUserRole";
import { apiService } from "../../services/api";
import UserRoleSelect from "./UserRoleSelect";
import TeamAssignmentChip from "./TeamAssignmentChip";
import AddTeamPopover from "./AddTeamPopover";
import AdminSearchHeader from "./AdminSearchHeader";
import AdminSnackbar, { AdminSnackState } from "./AdminSnackbar";

const MODULE_KEYS = [
  "dashboard",
  "tariff_calculator",
  "tariff_chat",
  "risk_prediction",
  "route_optimization",
  "admin_panel",
] as const;
const MODULE_LABELS: Record<string, string> = {
  dashboard: "Dashboard",
  tariff_calculator: "Tariff Calculator",
  tariff_chat: "Tariff Chat",
  risk_prediction: "Risk Prediction",
  route_optimization: "Route Optimization",
  admin_panel: "Admin Panel",
};

type RoleWithModules = { role_name: string; modules: Record<string, boolean> };

const UsersTab: React.FC = () => {
  const { isSuper, isAdmin } = useUserRole();
  const { data: users, isLoading, error } = useAdminUsers();
  const { data: teams } = useAdminTeams();
  const updateRole = useUpdateUserRole();
  const deactivateUser = useDeactivateUser();
  const addToTeam = useAddUserToTeam();
  const removeFromTeam = useRemoveUserFromTeam();
  const getUserModules = useGetUserModules();
  const setUserModules = useSetUserModules();

  const [search, setSearch] = useState("");
  const [snack, setSnack] = useState<AdminSnackState | null>(null);
  const [teamPopoverAnchor, setTeamPopoverAnchor] = useState<HTMLElement | null>(null);
  const [teamPopoverUserId, setTeamPopoverUserId] = useState<string>("");
  const [permDialogOpen, setPermDialogOpen] = useState(false);
  const [permTargetUserId, setPermTargetUserId] = useState<string | null>(null);
  const [permTargetRole, setPermTargetRole] = useState<"super_admin" | "admin" | "user" | null>(null);
  const [permModules, setPermModules] = useState<UserModuleAccess | null>(null);
  const [permLoading, setPermLoading] = useState(false);

  const [roleModulesList, setRoleModulesList] = useState<RoleWithModules[]>([]);
  const [loadingRoleModules, setLoadingRoleModules] = useState(false);
  const [editRolePermsOpen, setEditRolePermsOpen] = useState(false);
  const [editRoleName, setEditRoleName] = useState("");
  const [editRoleSelectedPerms, setEditRoleSelectedPerms] = useState<string[]>([]);

  const filtered = useMemo(() => {
    if (!users) return [];
    const q = search.toLowerCase();
    return users.filter(
      (u) =>
        u.email?.toLowerCase().includes(q) ||
        u.first_name?.toLowerCase().includes(q) ||
        u.last_name?.toLowerCase().includes(q) ||
        u.role?.toLowerCase().includes(q)
    );
  }, [users, search]);

  const handleRoleChange = async (clerkUserId: string, newRole: string) => {
    try {
      await updateRole.mutateAsync({ clerkUserId, role: newRole });
      setSnack({ msg: "Role updated", severity: "success" });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to update role";
      setSnack({ msg: message, severity: "error" });
    }
  };

  const handleDeactivate = async (clerkUserId: string) => {
    if (!globalThis.confirm("Deactivate this user? They will lose access.")) return;
    try {
      await deactivateUser.mutateAsync(clerkUserId);
      setSnack({ msg: "User deactivated", severity: "success" });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to deactivate";
      setSnack({ msg: message, severity: "error" });
    }
  };

  const handleAddTeam = (clerkUserId: string, e: React.MouseEvent<HTMLElement>) => {
    setTeamPopoverUserId(clerkUserId);
    setTeamPopoverAnchor(e.currentTarget);
  };

  const handleTeamSelect = async (teamId: number) => {
    try {
      await addToTeam.mutateAsync({ clerkUserId: teamPopoverUserId, teamId });
      setSnack({ msg: "Added to team", severity: "success" });
    } catch {
      setSnack({ msg: "Failed to add to team", severity: "error" });
    }
  };

  const handleRemoveTeam = async (clerkUserId: string, teamId: number) => {
    try {
      await removeFromTeam.mutateAsync({ clerkUserId, teamId });
      setSnack({ msg: "Removed from team", severity: "success" });
    } catch {
      setSnack({ msg: "Failed to remove from team", severity: "error" });
    }
  };

  const handleOpenPermissions = async (clerkUserId: string, role: "super_admin" | "admin" | "user") => {
    setPermTargetUserId(clerkUserId);
    setPermTargetRole(role);
    setPermLoading(true);
    setPermModules(null);
    setPermDialogOpen(true);
    try {
      const res = await getUserModules.mutateAsync(clerkUserId);
      setPermModules(res.modules);
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : "Failed to load module permissions for this user.";
      setSnack({ msg: message, severity: "error" });
      setPermDialogOpen(false);
    } finally {
      setPermLoading(false);
    }
  };

  const handleSavePermissions = async () => {
    if (!permTargetUserId || !permModules) return;
    try {
      await setUserModules.mutateAsync({
        clerkUserId: permTargetUserId,
        modules: permModules,
      });
      setSnack({ msg: "Permissions updated", severity: "success" });
      setPermDialogOpen(false);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to update permissions";
      setSnack({ msg: message, severity: "error" });
    }
  };

  const handleToggleModule = (key: keyof UserModuleAccess) => {
    setPermModules((prev) =>
      prev ? { ...prev, [key]: !prev[key] } : prev,
    );
  };

  const loadRoleModules = async () => {
    setLoadingRoleModules(true);
    try {
      const res = await apiService.getAdminRoles();
      setRoleModulesList(res.data.roles || []);
    } catch {
      setSnack({ msg: "Failed to load role permissions", severity: "error" });
    } finally {
      setLoadingRoleModules(false);
    }
  };

  useEffect(() => {
    if (isSuper || isAdmin) loadRoleModules();
  }, [isSuper, isAdmin]);

  const openEditRolePermsFromModules = (roleName: string, modules: Record<string, boolean>) => {
    setEditRoleName(roleName);
    setEditRoleSelectedPerms(MODULE_KEYS.filter((k) => modules[k]));
    setEditRolePermsOpen(true);
  };

  const toggleRolePerm = (permName: string) => {
    setEditRoleSelectedPerms((current) =>
      current.includes(permName)
        ? current.filter((p) => p !== permName)
        : [...current, permName]
    );
  };

  const handleSaveRolePerms = async () => {
    try {
      const modules: Record<string, boolean> = {};
      MODULE_KEYS.forEach((k) => {
        modules[k] = editRoleSelectedPerms.includes(k);
      });
      await apiService.setRoleModules(editRoleName, modules);
      setSnack({ msg: "Role permissions updated", severity: "success" });
      setEditRolePermsOpen(false);
      loadRoleModules();
    } catch {
      setSnack({ msg: "Failed to update role permissions", severity: "error" });
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={32} sx={{ color: "#EF2D3E" }} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Failed to load users. Check your permissions.</Alert>;
  }

  return (
    <Box>
      <AdminSearchHeader
        title="Users"
        count={filtered.length}
        searchPlaceholder="Search users..."
        search={search}
        onSearchChange={setSearch}
      />

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "#F9FAFB" }}>
              <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Teams</TableCell>
              {(isSuper || isAdmin) && (
                <TableCell sx={{ fontWeight: 600, width: 160 }}>Actions</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((user) => {
              const canEditPermissions =
                isSuper || (isAdmin && user.role === "user");
              return (
                <TableRow key={user.clerk_user_id} hover>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Avatar
                        src={user.image_url}
                        sx={{ width: 32, height: 32, fontSize: "0.85rem", bgcolor: "#E5E7EB" }}
                      >
                        {(user.first_name?.[0] || user.email?.[0] || "?").toUpperCase()}
                      </Avatar>
                      <Typography sx={{ fontSize: "0.85rem", fontWeight: 500 }}>
                        {user.first_name} {user.last_name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: "0.8rem", color: "#6B7280" }}>{user.email}</Typography>
                  </TableCell>
                  <TableCell>
                    <UserRoleSelect
                      value={user.role}
                      onChange={(r) => handleRoleChange(user.clerk_user_id, r)}
                      disabled={updateRole.isPending}
                      readOnly={!isSuper}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center" }}>
                      {(user.teams || []).map((t) => (
                        <TeamAssignmentChip
                          key={t.id}
                          teamName={t.name}
                          onRemove={isSuper ? () => handleRemoveTeam(user.clerk_user_id, t.id) : undefined}
                        />
                      ))}
                      {isSuper && (
                        <Tooltip title="Add to team">
                          <IconButton
                            size="small"
                            onClick={(e) => handleAddTeam(user.clerk_user_id, e)}
                            sx={{ ml: 0.5 }}
                          >
                            <AddIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  {(isSuper || isAdmin) && (
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                        {canEditPermissions && (
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleOpenPermissions(user.clerk_user_id, user.role)}
                            sx={{ textTransform: "none", fontSize: "0.75rem", fontFamily: "'Inter', sans-serif", color: "#374151", borderColor: "#EAEAEA", "&:hover": { borderColor: "#9CA3AF", bgcolor: "#F9FAFB" } }}
                          >
                            Permissions
                          </Button>
                        )}
                        {isSuper && (
                          <Tooltip title="Deactivate user">
                            <IconButton
                              size="small"
                              onClick={() => handleDeactivate(user.clerk_user_id)}
                              disabled={deactivateUser.isPending}
                              sx={{ color: "#EF2D3E", "&:hover": { bgcolor: "#FFF1F2" } }}
                            >
                              <PersonOffIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={isSuper ? 5 : 4} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No users found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Role permissions – default module access per role (super admin only) */}
      {(isSuper || isAdmin) && (
        <Box sx={{ mt: 3, pt: 2, borderTop: "1px solid #E5E7EB" }}>
          <Typography sx={{ fontWeight: 600, fontSize: "0.95rem", mb: 1 }}>
            Role permissions
          </Typography>
          <Typography variant="body2" sx={{ color: "#6B7280", mb: 1.5 }}>
            Default module access per role. Only super admins can edit these.
          </Typography>
          {loadingRoleModules ? (
            <Box sx={{ py: 2, display: "flex", justifyContent: "center" }}>
              <CircularProgress size={24} sx={{ color: "#EF2D3E" }} />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "#F9FAFB" }}>
                    <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Permissions</TableCell>
                    {isSuper && (
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {roleModulesList.map((r) => (
                    <TableRow key={r.role_name}>
                      <TableCell>
                        <Typography sx={{ fontWeight: 500, textTransform: "capitalize" }}>
                          {r.role_name.replaceAll("_", " ")}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" flexWrap="wrap" gap={0.5}>
                          {MODULE_KEYS.filter((k) => r.modules[k]).map((k) => (
                            <Chip key={k} label={MODULE_LABELS[k] || k} size="small" />
                          ))}
                          {MODULE_KEYS.filter((k) => r.modules[k]).length === 0 && (
                            <Typography variant="body2" color="text.secondary">
                              No modules
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                      {isSuper && (
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EditIcon fontSize="small" />}
                            onClick={() => openEditRolePermsFromModules(r.role_name, r.modules)}
                            sx={{ textTransform: "none", fontFamily: "'Inter', sans-serif", color: "#EF2D3E", borderColor: "#EF2D3E", "&:hover": { borderColor: "#d42635", bgcolor: "#FFF1F2" } }}
                          >
                            Edit
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* Permissions dialog */}
      <Dialog
        open={permDialogOpen}
        onClose={() => setPermDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Module Permissions</DialogTitle>
        <DialogContent dividers>
          {permLoading || !permModules ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={permModules.dashboard}
                    onChange={() => handleToggleModule("dashboard")}
                    size="small"
                  />
                }
                label="Dashboard"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={permModules.tariff_calculator}
                    onChange={() => handleToggleModule("tariff_calculator")}
                    size="small"
                  />
                }
                label="Tariff Calculator"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={permModules.tariff_chat}
                    onChange={() => handleToggleModule("tariff_chat")}
                    size="small"
                  />
                }
                label="Tariff Chat"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={permModules.risk_prediction}
                    onChange={() => handleToggleModule("risk_prediction")}
                    size="small"
                  />
                }
                label="Risk Prediction"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={permModules.route_optimization}
                    onChange={() => handleToggleModule("route_optimization")}
                    size="small"
                  />
                }
                label="Route Optimization"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={permModules.admin_panel}
                    onChange={() => handleToggleModule("admin_panel")}
                    size="small"
                    disabled={permTargetRole === "user" && !isSuper}
                  />
                }
                label="Admin Panel"
              />
            </FormGroup>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPermDialogOpen(false)} sx={{ textTransform: "none", fontFamily: "'Inter', sans-serif", color: "#6B7280" }}>Cancel</Button>
          <Button
            onClick={handleSavePermissions}
            variant="contained"
            disabled={permLoading || !permModules}
            sx={{ bgcolor: "#EF2D3E", "&:hover": { bgcolor: "#d42635" }, textTransform: "none", fontFamily: "'Inter', sans-serif" }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit role permissions dialog (default modules per role) */}
      <Dialog
        open={editRolePermsOpen}
        onClose={() => setEditRolePermsOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Role Permissions</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Role: <strong>{editRoleName.replaceAll("_", " ")}</strong>
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5, color: "#6B7280" }}>
            Select which modules this role can access by default.
          </Typography>
          <FormGroup>
            {MODULE_KEYS.map((k) => (
              <FormControlLabel
                key={k}
                control={
                  <Checkbox
                    size="small"
                    checked={editRoleSelectedPerms.includes(k)}
                    onChange={() => toggleRolePerm(k)}
                  />
                }
                label={<Typography variant="body2">{MODULE_LABELS[k] || k}</Typography>}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditRolePermsOpen(false)} sx={{ textTransform: "none", fontFamily: "'Inter', sans-serif", color: "#6B7280" }}>Cancel</Button>
          <Button onClick={handleSaveRolePerms} variant="contained" sx={{ bgcolor: "#EF2D3E", "&:hover": { bgcolor: "#d42635" }, textTransform: "none", fontFamily: "'Inter', sans-serif" }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <AddTeamPopover
        anchorEl={teamPopoverAnchor}
        onClose={() => setTeamPopoverAnchor(null)}
        teams={teams || []}
        assignedTeamIds={
          users?.find((u) => u.clerk_user_id === teamPopoverUserId)?.teams?.map((t) => t.id) || []
        }
        onSelect={handleTeamSelect}
      />

      <AdminSnackbar snack={snack} onClose={() => setSnack(null)} />
    </Box>
  );
};

export default UsersTab;
